import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { ArticleStructuredData } from '@/components/feature/SEOComponents';
import ComicCard from '@/components/feature/ComicCard';
import PageHead from '@/components/base/PageHead';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);

  if (!comic) {
    return (
      <>
        <PageHead
          title="漫画未找到 - 韩国漫画网"
          description="您访问的漫画不存在或已下架。韩国漫画网提供韩国漫画网站大全资源，海量免费韩漫在线看。"
        />
        <main className="px-4 md:px-6 py-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-background-100 mx-auto mb-4">
              <i className="ri-file-unknow-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">您访问的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 5);

  const categoryName = categories.find((c) => c.slug === comic.categorySlug)?.name || comic.category;
  const comicUrl = `https://www.koreancomic.cn/comic/${comic.id}`;
  const pageTitle = `${comic.title} - 韩国漫画网 | 免费韩漫在线看`;
  const pageDesc = `${comic.title}：${comic.shortDescription} 作者：${comic.author}，状态：${comic.status}，韩国漫画网提供免费韩漫在线看，支持下拉式阅读。`;

  return (
    <>
      <PageHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${comic.title}, 韩国漫画, ${comic.category}, 免费韩漫, 韩漫在线看, ${comic.author}`}
        canonicalUrl={comicUrl}
        ogType="article"
        ogImage={comic.cover}
      />

      <ArticleStructuredData
        title={comic.title}
        description={comic.description}
        url={comicUrl}
        imageUrl={comic.cover}
        authorName={comic.author}
        datePublished={comic.chapters[0]?.updateDate || '2025-01-01'}
        dateModified={comic.updateTime}
      />

      <main>
        {/* Breadcrumb */}
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/all' },
                { label: categoryName, href: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        {/* Comic Detail */}
        <section className="px-4 md:px-6 py-8 md:py-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 w-full md:w-64">
                <div className="rounded-xl overflow-hidden" style={{ aspectRatio: '3/4' }}>
                  <img
                    src={comic.cover}
                    alt={`${comic.title} 韩国漫画`}
                    title={`${comic.title} - 韩国漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">
                  作者：<strong>{comic.author}</strong>
                </p>

                {/* Meta */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                    {categoryName}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-background-200/70 text-foreground-600">
                    <i className="ri-star-fill text-accent-500 mr-1"></i>
                    {comic.rating}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-background-200/70 text-foreground-600">
                    <i className="ri-eye-line mr-1"></i>
                    {(comic.views / 10000).toFixed(1)}万人气
                  </span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-full text-xs bg-background-100 text-foreground-600">
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Description */}
                <div className="bg-background-100 rounded-xl p-4 md:p-5">
                  <h2 className="font-heading text-sm font-semibold text-foreground-950 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                {/* Chapters */}
                <div className="mt-6">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                    章节列表 <span className="text-sm text-foreground-400 font-normal">共{comic.chapters.length}话</span>
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {comic.chapters.map((chapter) => (
                      <div
                        key={chapter.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs font-medium text-foreground-950 whitespace-nowrap">
                            第{chapter.number}话
                          </span>
                          <span className="text-xs text-foreground-500 truncate">{chapter.title}</span>
                        </div>
                        <span className="text-xs text-foreground-400 ml-2 whitespace-nowrap">{chapter.updateDate}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  更多{categories.find(c => c.slug === comic.categorySlug)?.name || ''}漫画 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}